import { AgGridReact } from "ag-grid-react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import { useHistory, useLocation, useParams } from "react-router";
import { FilterChipComponentRef } from "entity-app/shared-components/log-render-components/filter-chip-component";
import { FeatureTypes } from "entity-app/constants";

import { RowStyle } from "ag-grid-enterprise";
import { useMaterialLogData } from "./hooks/material-feature-data";

import { useMaterialLogAgGrid } from "./hooks/material-ag-grid-data";
import MaterialHeaderItems from "./components/header-items";
import { MaterialExtractSpecType } from "./model";
import ExtractedPdfViewer from "./components/extracted-pdf-viewer";
import CreateMaterialModal from "./components/create-material-modal";

function MaterialExtractedLogPage() {
  const { state } = useLocation() as any;
  const history = useHistory();

  const gridRef = useRef<AgGridReact<any>>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [selectedExtraction, setExtraction] =
    useState<MaterialExtractSpecType>();
  const [extractionFilterId, setExtractionFilterId] = useState<
    string | undefined
  >();
  const [createMaterialRow, setCreateMaterialRow] =
    useState<MaterialExtractSpecType | null>(null);
  const [selectedRows, setSelectedRows] = useState<
    MaterialExtractSpecType[] | null
  >(null);

  const pageData = useMaterialLogData();
  const { entityList } = pageData;
  const { isProjectArchived } = useContext(ProjectContext);
  const { projectId } = useParams() as any;

  const agGridData = useMaterialLogAgGrid({
    pageData
  });

  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();

  useEffect(() => {
    if (gridRef.current?.api) {
      if (selectedExtraction) {
        const columns = extractionFilterId
          ? ["active", "ag-Grid-SelectionColumn"]
          : ["active"];
        gridRef.current.api.setColumnsVisible(columns, false);
      } else {
        gridRef.current.api.setColumnsVisible(
          ["active", "ag-Grid-SelectionColumn"],
          true
        );
      }
    }
  }, [selectedExtraction, extractionFilterId]);

  useEffect(() => {
    const extractionId = state?.extraction?.id;

    if (!extractionId || !entityList?.length) return;

    const rowData = entityList.find((row) => row.id === extractionId);
    if (!rowData) return;

    setExtraction(rowData);
    setExtractionFilterId(extractionId);

    setTimeout(() => {
      if (gridRef.current?.api) {
        gridRef.current.api.onFilterChanged();
      }
    }, 500);

    // Clear history state once used
    history.replace(history.location.pathname, {});
  }, [state?.extraction?.id, entityList, history]);

  // Listen for changes in the "active" field
  const onCellValueChanged = (params: any) => {
    if (params.colDef.field === "active") {
      // Update state so React re-renders (if you want controlled rowData)
      pageData.setEntityList((prev) => {
        if (!prev) return prev;
        return prev.map((row) =>
          row.id === params.data.id
            ? { ...row, active: params.data.active }
            : row
        );
      });
    }
  };

  const getRowStyle = useCallback(
    (params: any): RowStyle | undefined => {
      if (params.data.id === selectedExtraction?.id) {
        return {
          backgroundColor: "var(--ciq-blue-100)"
        };
      }
      return undefined;
    },
    [selectedExtraction?.id]
  );

  useEffect(() => {
    if (!selectedExtraction && gridRef?.current?.api)
      gridRef?.current?.api?.sizeColumnsToFit();
    setTimeout(() => {
      gridRef?.current?.api?.redrawRows();
    });
  }, [selectedExtraction]);

  useEffect(() => {
    if (gridRef.current && pageData.entityList && selectedExtraction?.id) {
      const api = gridRef.current?.api;
      const rowNode = api?.getRowNode(selectedExtraction.id);
      if (rowNode) {
        api?.ensureNodeVisible(rowNode, "middle"); // "top" | "bottom" | "middle"
      }
    }
  }, [pageData.entityList, selectedExtraction?.id]);

  const onClosePdfViewer = () => {
    setExtraction(undefined);
    setExtractionFilterId(undefined);
    gridRef.current?.api?.onFilterChanged();
    // gridRef.current?.api?.deselectAll();
  };

  return (
    <div className="flex flex-col h-full w-full">
      <MaterialHeaderItems
        gridRef={gridRef}
        pageData={pageData}
        filterChipRef={filterChipRef}
        statsData={statsData}
        selectedRows={selectedRows}
        projectId={projectId}
        isGridReady={isGridReady}
        selectedExtraction={selectedExtraction}
        extractionFilterId={extractionFilterId}
        onClosePdfViewer={onClosePdfViewer}
        extractionHistory={pageData.extractionHistoryData}
      />
      <div className="flex h-full w-full">
        <div
          className="flex flex-col h-full"
          style={{ width: selectedExtraction ? 460 : "100%" }}
        >
          <AgGridReact<MaterialExtractSpecType>
            ref={gridRef}
            className="ag-theme-alpine"
            rowData={pageData.entityList}
            columnDefs={agGridData.columnDefs}
            defaultColDef={agGridData.defaultColDef}
            rowHeight={62}
            onFirstDataRendered={() => {
              setGridReady(true);
              // e.api.sizeColumnsToFit();
            }}
            onCellValueChanged={onCellValueChanged}
            getRowId={(param) => param?.data?.id}
            getRowStyle={getRowStyle}
            rowSelection={{ mode: "multiRow", selectAll: "filtered" }}
            context={{
              isProjectArchived,
              onCreateMaterial: (data: MaterialExtractSpecType) => {
                if (isProjectArchived) return;
                setCreateMaterialRow(data);
              }
            }}
            onCellClicked={(e) => {
              const colId = e?.colDef?.colId || "";
              if (!["ag-Grid-SelectionColumn", "active"].includes(colId)) {
                setExtraction(e.data);
              }
            }}
            onSelectionChanged={() => {
              const gridSelectedRows =
                (gridRef?.current?.api?.getSelectedRows() as MaterialExtractSpecType[]) ||
                [];
              setSelectedRows(
                gridSelectedRows.length ? gridSelectedRows : null
              );
            }}
            onFilterChanged={() => {
              filterChipRef.current?.onFilterApplied();
              setStatsData(
                calculateLogPageStats({
                  gridRef,
                  ListFilter: undefined,
                  featureTypeId: FeatureTypes.MATERIALS
                })
              );
            }}
            isExternalFilterPresent={() => !!extractionFilterId}
            doesExternalFilterPass={(rowNode) => {
              if (!extractionFilterId) return true;
              const { data } = rowNode;
              if (!data) return false;
              const isParent = !!data.grouped_parent?.some(
                (parent) => parent.parent_extraction_id === extractionFilterId
              );
              return data.id === extractionFilterId || isParent;
            }}
          />
        </div>
        {selectedExtraction && (
          <ExtractedPdfViewer
            extrationData={selectedExtraction}
            onClose={onClosePdfViewer}
          />
        )}
      </div>
      {createMaterialRow && (
        <CreateMaterialModal
          open={!!createMaterialRow}
          onCancel={() => setCreateMaterialRow(null)}
          onComplete={() => {
            setCreateMaterialRow(null);
          }}
          selectedRow={createMaterialRow}
          materialHeaderMap={pageData.materialHeaderMap}
        />
      )}
    </div>
  );
}

export default MaterialExtractedLogPage;
